import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { MiddlewareAPI, Middleware } from '@reduxjs/toolkit';
import { Notify } from 'components/Notifications/Notifications';

// Prevent the same status-code toast from firing multiple times when several
// concurrent requests all fail with the same error (e.g. 401 on page load).
const recentlyShown = new Set<number | string>();
function showOnce(key: number | string, message: string) {
  if (recentlyShown.has(key)) return;
  recentlyShown.add(key);
  Notify.error(message);
  setTimeout(() => recentlyShown.delete(key), 5000);
}

export const rtkQueryErrorHandler: Middleware =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (api: MiddlewareAPI) => (next) => (action) => {
    if (isRejectedWithValue(action)) {
      const payload = action.payload as { status?: number | string; data?: { message?: string } } | undefined;
      const status = payload?.status;

      // Network / parsing failures have a string status (e.g. 'FETCH_ERROR', 'PARSING_ERROR').
      if (typeof status === 'string') {
        showOnce(status, 'A network error occurred. Please check your connection and try again.');
        return next(action);
      }

      if (status == null) return next(action);

      // HTTP error responses
      const isMutation = typeof action.type === 'string' && action.type.endsWith('/executeMutation/rejected');

      if (status >= 500) {
        showOnce(status, 'An error occurred. Please try again.');
      } else if (status === 401) {
        showOnce(401, 'Your session has expired. Please reload the page and log in again.');
      } else if (status === 403) {
        showOnce(403, 'You do not have permission to perform this action.');
      } else if (status === 404) {
        // API endpoint or resource not found — show once to avoid duplicate toasts
        // when multiple concurrent queries hit the same misconfigured endpoint.
        showOnce(404, 'The requested content could not be found. Please try refreshing.');
      } else if ((status === 422 || status === 400) && !isMutation) {
        // Only show global 400/422 toasts for background queries — mutations handle
        // their own validation errors at the call site to avoid duplicate toasts.
        const data = payload?.data;
        Notify.error(data?.message ?? 'The request was invalid. Please check your input and try again.');
      }
    }

    return next(action);
  };

export default rtkQueryErrorHandler;
